import type { Product } from "@stockline/shared/types/inventory/product"
import { offlineDb } from "../../../lib/offline/db"
import { PRODUCT_TABLE } from "../../../lib/offline/resources/inventory/product/db"
import { useSharedLiveQuery } from "../../../lib/offline/use-shared-live-query"

export interface UseProductsFilter {
  isSleeping?: boolean
  companyId?: string
}

const EMPTY_PRODUCTS: Product[] = []

function productsCacheKey(filter?: UseProductsFilter): string {
  return filter
    ? `products:isSleeping=${filter.isSleeping ?? ""}:companyId=${filter.companyId ?? ""}`
    : "products:all"
}

function productsQueryFactory(filter?: UseProductsFilter) {
  return async () => {
    const table = offlineDb.table<Product>(PRODUCT_TABLE)
    // `name` isn't indexed — fetch then JS-sort to mirror the legacy
    // `reactive.useQuery` behaviour (Dexie `orderBy` would throw on a
    // non-indexed key). Products table is small enough that the cost is
    // negligible and the shared cache keeps it to once per unique filter.
    const all = await table.toArray()
    // Tombstone filter: legacy DELETE + delta-pull upserts soft-deleted
    // rows back into Dexie with `deletedAt` set. Hiding them here is
    // what makes the SSE delete event visibly remove the product.
    const live = all.filter(
      (p) => !(p as { deletedAt?: string | null }).deletedAt
    )
    const filtered = filter
      ? live.filter((p) => {
          if (
            filter.isSleeping !== undefined &&
            (p as { isSleeping?: boolean }).isSleeping !== filter.isSleeping
          ) {
            return false
          }
          if (
            filter.companyId !== undefined &&
            p.companyId !== filter.companyId
          ) {
            return false
          }
          return true
        })
      : live
    return filtered.sort((a, b) => {
      const an = (a as Product & { name?: string }).name ?? ""
      const bn = (b as Product & { name?: string }).name ?? ""
      return an.localeCompare(bn)
    })
  }
}

/**
 * Reactive global products list. Shared across all consumers via
 * `useSharedLiveQuery` so a remount (virtualizer scroll-out + back,
 * collapsible toggle, route change) reuses the cached value and paints
 * instantly with no Dexie roundtrip. The recap especially benefits — every
 * `<ProductCell>` and every `<PartnerButton>` reads from this list, so a
 * partner card scrolling into view doesn't pay a fresh fetch.
 */
export function useProducts(filter?: UseProductsFilter): Product[] {
  const rows = useSharedLiveQuery<Product[]>(
    productsCacheKey(filter),
    productsQueryFactory(filter)
  )
  return rows ?? EMPTY_PRODUCTS
}

/**
 * Whether the shared products cache for `filter` has not yet emitted its
 * first value — i.e. Dexie hydration is still in flight. Shares the exact
 * same cache key (and query) as `useProducts`, so `useSharedLiveQuery`'s
 * per-key dedup means calling both hooks for the same filter in one
 * component costs nothing extra: the underlying `liveQuery`/IDB scan runs
 * once, whichever hook mounts first creates the shared entry, and the other
 * just reads/subscribes to it.
 *
 * Consumers that need to distinguish "still loading" from "genuinely empty
 * catalog" (e.g. a picker that says "Loading…" instead of "No matches")
 * should use this instead of inferring loading from `options.length === 0`,
 * which would hang forever on an empty-but-loaded catalog.
 */
export function useProductsLoading(filter?: UseProductsFilter): boolean {
  const rows = useSharedLiveQuery<Product[]>(
    productsCacheKey(filter),
    productsQueryFactory(filter)
  )
  return rows === undefined
}
